import {
  AlarmExclamationRegularV6,
  BroomSolidV6,
  CheckSolidV6,
  FireFlameCurvedSolidV6,
  MagnifyingGlassSolidV6,
  PlusSolidV6,
  SeedlingSolidV6,
  SunSolidV6,
} from "@shared/v2/Icomoon";

export const DITL_SECTIONS = [
  {
    id: "start_your_day",
    label: "Start Your Day",
    icon: SunSolidV6,
    iconClassName: "tw-text-semantic-yellow-100",
  },
  {
    id: "newest",
    label: "Newest",
    icon: AlarmExclamationRegularV6,
    iconClassName: "tw-text-semantic-green-100",
  },
  {
    id: "high_priority",
    label: "High Priority",
    icon: FireFlameCurvedSolidV6,
    iconClassName: "tw-text-[var(--brivity-coral-60)]",
  },
  {
    id: "priority_1_tasks",
    label: "Priority 1 Tasks",
    icon: CheckSolidV6,
    iconClassName: "tw-text-[var(--brivity-blue-100)]",
  },
  {
    id: "future_business",
    label: "Future Business",
    icon: SeedlingSolidV6,
    iconClassName: "tw-text-semantic-green-100",
  },
  {
    id: "prospecting",
    label: "Prospecting",
    icon: MagnifyingGlassSolidV6,
    iconClassName: "tw-text-semantic-blue-100",
  },
  {
    id: "post_lead_gen",
    label: "Post Lead Gen",
    icon: BroomSolidV6,
    iconClassName: "tw-text-semantic-purple-50",
  },
];

const PREVIEW_AND_CALL = ["preview", "call"];
const PREVIEW_AND_GO_TO_TASKS = ["preview", "goToTasks"];

export const DITL_CARDS = {
  review_new_listings: {
    section: "start_your_day",
    type: "task",
    title: "Review New Listings",
    description:
      "Review all new listings that have come onto the market in the last 24 hours in your area of service.",
    actionLabel: "Review Listings",
    actionIcon: MagnifyingGlassSolidV6,
  },
  add_new_leads: {
    section: "start_your_day",
    type: "task",
    title: "Add New Leads to your Database",
    description: "Every contact belongs in Brivity! Now is the time to add them.",
    actionLabel: "Add a Lead",
    actionIcon: PlusSolidV6,
  },
  new_leads: {
    section: "newest",
    type: "urgent",
    title: "New Leads",
    description: "Leads marked as “New” with no call in the last 24 hours.",
  },
  recently_added: {
    section: "newest",
    type: "urgent",
    title: "Recently Added",
    collapsedTitle: "Recently Added - No Contact",
    description:
      "Leads who have been added in the last 2 weeks who have neither been called in the last 24 hours nor contacted in the last 2 weeks.",
  },
  hot_leads_and_prospective_clients: {
    section: "high_priority",
    type: "callList",
    title: "Hot Leads and Prospective Clients",
    collapsedTitle: "Hot Lead & Prospective Clients",
    description: "Hot leads and Prospective Clients that haven't been called in the last 7 days.",
    buttons: PREVIEW_AND_CALL,
  },
  gabbi_isa_needs_agent: {
    section: "high_priority",
    type: "callList",
    title: "Gabbi ISA Needs Agent",
    description:
      "Leads where Gabbi, your AI ISA, has engaged and flagged that a human agent should take over.",
    buttons: ["preview"],
    previewLabel: "View in Message Center",
  },
  a_a_plus_sphere_of_influence: {
    section: "high_priority",
    type: "callList",
    title: "A/A+ Sphere of Influence",
    description:
      "Contacts with tag “Sphere A” or “Sphere A+” who have not received a call in the last 30 days.",
    buttons: PREVIEW_AND_CALL,
  },
  market_report_viewers: {
    section: "high_priority",
    type: "callList",
    title: "Market Report Viewers",
    description: "Leads who have viewed Market Reports in the last 30 days who haven't been called.",
    buttons: PREVIEW_AND_CALL,
  },
  website_visitors: {
    section: "high_priority",
    type: "callList",
    title: "Website Visitors",
    description: "Leads who have visited your website in the last 30 days who haven't been called.",
    buttons: PREVIEW_AND_CALL,
  },
  leads_with_tasks_due_today: {
    section: "priority_1_tasks",
    type: "callList",
    title: "Leads with Tasks Due Today",
    description: "Priority 1 tasks that are due today.",
    descriptionVariant: "body-s",
    buttons: PREVIEW_AND_GO_TO_TASKS,
  },
  leads_with_overdue_tasks: {
    section: "priority_1_tasks",
    type: "callList",
    title: "Leads with Overdue Tasks",
    description: "Priority 1 tasks that are overdue.",
    descriptionVariant: "body-s",
    buttons: PREVIEW_AND_GO_TO_TASKS,
  },
  past_clients: {
    section: "future_business",
    type: "callList",
    title: "Past Clients",
    description: "Past clients who haven't been called in the last 90 days.",
    buttons: PREVIEW_AND_CALL,
  },
  b_sphere_of_influence: {
    section: "future_business",
    type: "callList",
    title: "“B” Sphere of Influence",
    description: "Contacts with tag “Sphere B” who haven't gotten a call in the last 90 days.",
    buttons: PREVIEW_AND_CALL,
  },
  nurture_leads: {
    section: "future_business",
    type: "callList",
    title: "Nurture Leads",
    description: "Nurture leads who haven't been called in the past 30 days.",
    buttons: PREVIEW_AND_CALL,
  },
  watch_leads: {
    section: "future_business",
    type: "callList",
    title: "Watch Leads",
    description: "Watch leads who haven't been called in the past 90 days.",
    buttons: PREVIEW_AND_CALL,
  },
  unqualified_leads_called_no_contact_7_days: {
    section: "future_business",
    type: "callList",
    title: "Unqualified (No call in 7 days)",
    description:
      "Unqualified leads who haven't been called in the last 7 days. Update their status if contact is made.",
    buttons: PREVIEW_AND_CALL,
  },
  unqualified_leads_called_never_contacted: {
    section: "future_business",
    type: "callList",
    title: "Unqualified (Called, Not Contacted)",
    description:
      "Unqualified leads who have been called but not successfully contacted in the last 7 days. Update their status if contact is made.",
    buttons: PREVIEW_AND_CALL,
  },
  c_sphere_of_influence: {
    section: "future_business",
    type: "callList",
    title: "“C” Sphere of Influence",
    description: "Contacts with tag “Sphere C” who haven't gotten a call in the last 6 months.",
    buttons: PREVIEW_AND_CALL,
  },
  circle_prospecting: {
    section: "prospecting",
    type: "task",
    title: "Circle Prospecting",
    description:
      "Find Sellers: Generate new leads by reaching out to neighbors around a recent listing or open house.",
    actionLabel: "Review Listings",
    actionIcon: MagnifyingGlassSolidV6,
  },
  call_fsbos_and_expireds: {
    section: "prospecting",
    type: "task",
    title: "Call FSBOs and Expireds",
    collapsedTitle: "FSBO and Expired Listings",
    description:
      "For Sale By Owner Listings: Call new FSBOs to target sellers who may be looking for a listing agent. Expired Listings: Every contact belongs in Brivity! Now is the time to add them.",
    actionLabel: "Review FSBO Listings",
    actionIcon: MagnifyingGlassSolidV6,
  },
  active_clients: {
    section: "post_lead_gen",
    type: "callList",
    title: "Active Clients",
    description: "Active clients who haven't been called in the last 7 days.",
    buttons: PREVIEW_AND_CALL,
  },
  leads_missing_listing_alerts: {
    section: "post_lead_gen",
    type: "callList",
    title: "Leads Missing Listing Alerts",
    description:
      "Create Listing Alerts for any leads who don't have one, driving engagement to your website.",
    buttons: ["preview"],
  },
  leads_missing_market_reports: {
    section: "post_lead_gen",
    type: "callList",
    title: "Leads Missing Market Reports",
    description: "Create Market Reports for any leads who don't have one, driving engagement to your emails.",
    buttons: ["preview"],
  },
};

export const toDitlCards = (sections) =>
  sections
    .filter(({ section }) => DITL_CARDS[section])
    .map(({ section, completed }) => ({ id: section, name: DITL_CARDS[section].title, completed }));
